'use client';

import { FC, useMemo, useContext, useState, useEffect } from 'react';
import { EntityTypeSelectorProps } from './EntityTypeSelector.types';
import {
  Flex,
  Input,
  Button,
  Select,
  ButtonGroup,
  type SelectItem,
} from '@/_components';
import { ENTITY_TYPES } from '@/_constants/entities';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getEntityTypesWithPlaceholders } from '@/_utils/entities';

const CUSTOM_ENTITY_TYPE = 'custom_entity_type';
const DEFAULT_ENTITY_TYPE: string = ENTITY_TYPES[0];

export const EntityTypeSelector: FC<EntityTypeSelectorProps> = ({
  isDisabled,
  onChange,
  shouldNotAllowIndividual,
  value,
}) => {
  const { firmEntityTypes } = useContext(FirmSettingsContext);

  const entityTypesWithPlaceholders =
    getEntityTypesWithPlaceholders(firmEntityTypes);

  const [isCustom, setCustom] = useState<boolean>(false);
  const [customEntityType, setCustomEntityType] = useState<string>(value || '');
  const [entityType, setEntityType] = useState<string>(
    value || DEFAULT_ENTITY_TYPE,
  );

  useEffect(() => {
    if (!!value) {
      setEntityType(value);
      setCustomEntityType(value);
    }
  }, [value]);

  useEffect(() => {
    if (!value) {
      onChange(DEFAULT_ENTITY_TYPE);
    }
  }, []); // eslint-disable-line

  const entityTypeOptions: Array<SelectItem> = useMemo(() => {
    const options = entityTypesWithPlaceholders?.map((et) => ({
      name: et,
      value: et,
    }));

    const finalOptions = [
      ...options,
      { name: 'New Custom Entity', value: CUSTOM_ENTITY_TYPE },
    ];

    if (shouldNotAllowIndividual) {
      const individual = finalOptions.find(
        (option) => option.value === 'Individual',
      );

      if (individual) {
        individual.name = 'Individual (Not Allowed)';
      }
    }

    return finalOptions;
  }, [entityTypesWithPlaceholders, shouldNotAllowIndividual]);

  const onCustomEntityTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue: string = event.target.value;
    onChange(newValue);

    if (!newValue) {
      setCustomEntityType('');
      setEntityType('');
    }
  };

  const onShowEntityTypeSelector = () => setCustom(false);

  const onEntityTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newEntityType: string = event.target.value;

    if (shouldNotAllowIndividual && newEntityType === 'Individual') {
      return onChange(value);
    }

    if (newEntityType === CUSTOM_ENTITY_TYPE) {
      setCustom(true);
      setEntityType('');
      setCustomEntityType('');
    } else {
      onChange(newEntityType);
    }
  };

  return (
    <Flex className="wp-100">
      {isCustom ? (
        <Flex isColumn={true} gap={4} className="wp-100">
          <Input
            label="Entity Type"
            isRequired={true}
            value={customEntityType}
            autoFocus={true}
            placeholder="New Entity Type"
            onChange={onCustomEntityTypeChange}
          />
          <ButtonGroup>
            <Button
              size="tiny"
              intent="secondary"
              value="Show All"
              onClick={onShowEntityTypeSelector}
            />
          </ButtonGroup>
        </Flex>
      ) : (
        <Select
          items={entityTypeOptions}
          label="Entity Type"
          selectedValue={entityType}
          isDisabled={isDisabled}
          onChange={onEntityTypeChange}
        />
      )}
    </Flex>
  );
};
